/**
 * SchemaAddFields' MODEL — the schema reading and the candidate building, with no React in it.
 *
 * The panel (`SchemaAddFields.tsx`) is a form and a set of refusals. Everything below is the
 * part that has an answer independent of any rendering: what fields a published schema already
 * has, which of its nodes an addition may join, what one addition's wire node and path are, and
 * what document the impact gate should be asked about. That is the split the Data area already
 * uses three times over — `DataSetForm`/`dataFormModel`, `DataArea`/`dataDocumentEdit`,
 * `DataReferencePicker`/`dataReferenceChips` — and the reason is testability: `candidateSchema`'s
 * refusal paths and `walkSchema`'s descent through an array's `items` are only reachable from the
 * page through a rendered form, so in practice they went untested.
 *
 * NOTHING HERE IS THE AUTHORITY. Go's `parseAdditionPath` owns the addition grammar and the
 * canonical validator owns the transition rule (ADR-168 D3/D4). This module builds the D7 impact
 * PREVIEW and the read-only display, and where it disagrees with the server it loses.
 */

import type { SchemaAddition } from '../api/client'
import type { FieldType } from './schemaDraft'

/** The keywords a node carries that are NOT the field's own identity — everything here is
 *  displayed as a read-only constraint. Left out are the two structural keys the walk itself
 *  descends and the three the display already renders in its own columns. */
const NOT_A_CONSTRAINT = new Set(['type', 'description', 'properties', 'items', 'required', '$id'])

/** One pre-existing field, as text. Nothing here is editable, so nothing here is a model. */
export interface ExistingField {
  /** The addition grammar's path: `properties[].contact.email`. */
  path: string
  type: string
  required: boolean
  description?: string
  /** Every other keyword, `enum` and `pattern` included, as `key`/`value` text. */
  constraints: { key: string; value: string }[]
}

/** One step of the addition grammar, STRUCTURALLY rather than as text: the property name plus
 *  whether the step descends that property's `items`. The walk already knows both while it is
 *  standing on the node, so nothing downstream has to re-derive them from the path string. */
export interface PathSegment {
  key: string
  /** True for a keyed collection's record — the `[]` suffix in the text form. */
  items: boolean
}

/** A node that already carries a `properties` map — the only place D4 admits an addition. */
export interface AddableNode {
  /** `''` for the document root; otherwise the ancestor path a new field's `path` starts with.
   *  This is the TEXT form, for the wire and for the operator; it is never re-parsed. */
  path: string
  /** The same descent, structurally — the walk's own route from the document root to `node`.
   *  Carrying it is what keeps the addition grammar in ONE place here: `candidateSchema` places
   *  an addition by replaying these steps on its copy instead of re-reading `path`. */
  segments: PathSegment[]
  /** The schema node at `path`, in the ORIGINAL document. Held for identity and for reading;
   *  the candidate is a copy, so this node is never written to. */
  node: Record<string, unknown>
}

export function isObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v)
}

/** Render a keyword's value for reading. Arrays are joined because an `enum` is the common case
 *  and `["free","paid"]` reads worse than `free, paid`; anything else falls back to JSON so a
 *  shape this display did not anticipate is still legible rather than `[object Object]`. */
export function constraintText(value: unknown): string {
  if (Array.isArray(value)) return value.map(v => (typeof v === 'string' ? v : JSON.stringify(v))).join(', ')
  if (typeof value === 'string') return value
  return JSON.stringify(value)
}

/**
 * Walk the schema, collecting the fields to display and the nodes an addition may join.
 *
 * The path grammar is the endpoint's own (`parseAdditionPath`): a dot descends an object's
 * property, `[]` descends a keyed collection's `items`. Building the offers from the DOCUMENT
 * rather than from a fixed depth is what makes "at the nesting depth the document already uses"
 * true of any schema instead of only of the flat ones.
 */
export function walkSchema(
  node: unknown, prefix: string, segments: PathSegment[],
  fields: ExistingField[], addable: AddableNode[],
): void {
  if (!isObject(node)) return
  const props = node.properties
  if (!isObject(props)) return
  addable.push({ path: prefix, segments, node })
  const required = Array.isArray(node.required) ? node.required : []
  for (const [name, rawChild] of Object.entries(props)) {
    const path = prefix ? `${prefix}.${name}` : name
    if (!isObject(rawChild)) {
      // A non-object property is malformed rather than absent, and saying so is more useful
      // than skipping it silently.
      fields.push({ path, type: 'not a schema object', required: required.includes(name), constraints: [] })
      continue
    }
    fields.push({
      path,
      type: typeof rawChild.type === 'string' ? rawChild.type : constraintText(rawChild.type),
      required: required.includes(name),
      description: typeof rawChild.description === 'string' ? rawChild.description : undefined,
      constraints: Object.entries(rawChild)
        .filter(([k]) => !NOT_A_CONSTRAINT.has(k))
        .map(([k, v]) => ({ key: k, value: constraintText(v) })),
    })
    if (rawChild.type === 'array' && isObject(rawChild.items)) {
      walkSchema(rawChild.items, `${path}[]`, [...segments, { key: name, items: true }],
        fields, addable)
    } else {
      walkSchema(rawChild, path, [...segments, { key: name, items: false }], fields, addable)
    }
  }
}

/** A child of a new `object` field. `required` exists so the form can REFUSE it by name — the
 *  rule the operator would otherwise meet as a 422 twenty seconds later. */
export interface DraftChild {
  name: string
  type: FieldType
  required: boolean
}

export interface DraftAddition {
  /** Stable for this draft's lifetime, and never sent: it is the row's React key. An array index
   *  cannot do that job — removing an earlier row would slide every later row onto a different
   *  component instance, carrying that instance's own state (the open help panel) onto a field it
   *  does not describe. The patch and remove callbacks still close over the index, which is safe
   *  because they are invoked synchronously against the list the render read. */
  id: string
  /** Which addable node it joins. */
  parent: string
  name: string
  type: FieldType
  description: string
  unit: string
  sentinel: string
  children: DraftChild[]
}

/** The wire node, in the key order the backend splices verbatim. Empty inputs are OMITTED —
 *  `"x-unit": ""` is a value the validator refuses, and an operator who left a box blank did
 *  not ask for one. */
export function nodeOf(a: DraftAddition): Omit<SchemaAddition, 'path'> {
  const node: Record<string, unknown> = { type: a.type }
  if (a.description.trim()) node.description = a.description.trim()
  if (a.unit.trim()) node['x-unit'] = a.unit.trim()
  if (a.sentinel.trim()) node['x-unknown-sentinel'] = a.sentinel.trim()
  if (a.type === 'object') {
    const props: Record<string, unknown> = {}
    for (const c of a.children) {
      if (c.name.trim()) props[c.name.trim()] = { type: c.type }
    }
    node.properties = props
  }
  return node
}

export function pathOf(a: DraftAddition): string {
  const name = a.name.trim()
  return a.parent ? `${a.parent}.${name}` : name
}

/**
 * THE CANDIDATE SCHEMA — the document the impact check has to be asked about.
 *
 * The impact endpoint answers "would this break a consumer", and it can only answer it about a
 * whole document. The record editor's call (`DataArea.tsx:182`) sends `data` only, which cannot
 * answer this question at all: what is being published here is a SCHEMA the consumers have never
 * been validated against. So this panel builds its own candidate — a deep copy of the published
 * document with the additions spliced in — and asks about that.
 *
 * This is a PREVIEW, not the authority. The server patches the real bytes itself (ADR-168 D3)
 * and the canonical validator re-checks the transition; a candidate built here that disagrees
 * with the server's patch loses. Its only job is to make the impact answer be about the change
 * the operator is actually looking at.
 *
 * THE PATH IS NOT RE-PARSED HERE. `walkSchema` already descended this exact structure to emit
 * the offers, so each offer carries the walk's own route (`AddableNode.segments`). This replays
 * those steps on the COPY — the addition has to land in the candidate, not in the published
 * document — which leaves the grammar itself stated once in this file, and twice in the codebase:
 * Go's `parseAdditionPath` is the authority, this walk is the D7 impact preview.
 *
 * Returns `null` when an addition names a parent that is not on offer, or when the copy's shape
 * does not answer the walk's route — a candidate that silently dropped an addition would produce
 * a CLEAN verdict for a change that was never in it.
 */
export function candidateSchema(
  document: unknown, additions: DraftAddition[], addable: AddableNode[],
): unknown {
  if (!isObject(document)) return null
  const root = JSON.parse(JSON.stringify(document)) as Record<string, unknown>
  for (const a of additions) {
    const name = a.name.trim()
    if (!name) return null
    const target = addable.find(n => n.path === a.parent)
    if (!target) return null
    let node: Record<string, unknown> = root
    for (const seg of target.segments) {
      const props = node.properties
      if (!isObject(props)) return null
      const child = props[seg.key]
      if (!isObject(child)) return null
      node = child
      if (seg.items) {
        if (!isObject(node.items)) return null
        node = node.items
      }
    }
    if (!isObject(node.properties)) return null
    ;(node.properties as Record<string, unknown>)[name] = nodeOf(a)
  }
  return root
}

/**
 * The panel's publish options — everything the operator chooses ABOUT the publish rather than
 * about a field.
 */
export interface PublishOptions {
  /** Move the consumers that pin the current set onto the new version. */
  repin: boolean
}

/**
 * THE REPIN IS ON BY DEFAULT. A publish that leaves every consumer pinned to the version it
 * replaced is the state this action kept producing, and an operator who reads the form and
 * touches nothing should get the move rather than the litter.
 *
 * A function rather than a shared constant: the panel holds this as React state and patches it,
 * so a single module-level object would be written to by every panel that ever rendered.
 */
export function defaultPublishOptions(): PublishOptions {
  return { repin: true }
}
